import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Gavel, MessageSquare, AlertOctagon, Check, ChevronDown } from "lucide-react";
import { fmtDate } from "@/lib/workflow";

const KIND = { call: { label: "Call note", icon: Phone }, decision: { label: "Decision", icon: Gavel }, message: { label: "Message", icon: MessageSquare } };

export const PhaseNotes = ({ project, phase, users, onChanged }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("message");
  const [issue, setIssue] = useState({ text: "", owner_id: "", due_date: "", blocking: true });
  const openIssues = phase.blockers.filter((b) => b.open);
  const openBlocking = openIssues.filter((b) => b.blocking ?? b.critical);
  const [issuesOpen, setIssuesOpen] = useState(false);

  const load = () => api.get(`/projects/${project.project_id}/phases/${phase.phase_id}/notes`).then((r) => setNotes(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [phase.phase_id]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/projects/${project.project_id}/phases/${phase.phase_id}/notes`, { text: text.trim(), kind });
      setText("");
      await load();
      if (data.mentions?.length) toast.success(`Sent to inbox: ${data.mentions.map((m) => m.name).join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const addBlocker = async () => {
    if (!issue.text.trim()) return;
    try {
      await api.post(`/projects/${project.project_id}/phases/${phase.phase_id}/blockers`, { text: issue.text.trim(), blocking: issue.blocking, owner_id: issue.owner_id || phase.owner_id || null, due_date: issue.due_date || null });
      setIssue({ text: "", owner_id: "", due_date: "", blocking: true });
      await onChanged();
      toast.success(issue.blocking ? "Blocking issue recorded — phase marked Blocked" : "Issue recorded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const resolve = async (b) => {
    await api.patch(`/projects/${project.project_id}/phases/${phase.phase_id}/blockers/${b.blocker_id}/resolve`);
    await onChanged();
  };

  const renderText = (t) => t.split(/(@[A-Z][\w]+(?: [A-Z][\w]+)?)/g).map((part, i) =>
    part.startsWith("@") ? <span key={i} className="text-blue-700 font-medium">{part}</span> : part);

  return (
    <section className="grid lg:grid-cols-3 gap-5" data-testid="phase-notes-section">
      <div className="lg:col-span-2">
        <p className="overline">Notes & activity</p>
        <h3 className="text-base font-semibold text-slate-900">Calls, decisions and messages for {phase.name}</h3>
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="note-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(KIND).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea rows={3} className="mt-2" value={text} onChange={(e) => setText(e.target.value)} placeholder="Record a call, a decision or write a message. Use @Name to notify someone." data-testid="phase-note-input" />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={send} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="phase-note-send-btn">Post</Button>
          </div>
        </div>
        <div className="mt-3 space-y-2 max-h-[420px] overflow-auto pr-1">
          {notes.length === 0 ? <p className="text-xs text-slate-500">Nothing recorded for this phase yet.</p> : notes.map((n) => {
            const Icon = KIND[n.kind]?.icon || MessageSquare;
            return (
              <div key={n.note_id} className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`note-row-${n.note_id}`}>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /><span className="font-medium text-slate-700">{n.user_name}</span><Badge variant="outline" className="text-[10px]">{KIND[n.kind]?.label || n.kind}</Badge>
                    <button onClick={async () => { await api.patch(`/projects/${project.project_id}/notes/${n.note_id}/share`, null, { params: { shared: !n.shared_with_customer } }); await load(); }} className={`rounded-full border px-2 py-0.5 text-[10px] transition ${n.shared_with_customer ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-400 border-slate-200 hover:text-slate-700"}`} title="Toggle visibility in Customer Report" data-testid={`note-share-toggle-${n.note_id}`}>{n.shared_with_customer ? "Shared with customer" : "Internal only"}</button>
                  </span>
                  <span className="font-mono">{fmtDate(n.created_at)}</span>
                </div>
                <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{renderText(n.text)}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <button onClick={() => setIssuesOpen((o) => !o)} className="w-full flex items-center justify-between text-left rounded-md hover:bg-slate-50 transition-colors" aria-expanded={issuesOpen} data-testid="issues-toggle-btn">
          <div>
            <p className="overline">Issues & Blockers</p>
            <h3 className="text-base font-semibold text-slate-900">{openIssues.length} open{openBlocking.length > 0 && <span className="text-red-600"> · {openBlocking.length} blocking</span>}</h3>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${issuesOpen ? "rotate-180" : ""}`} />
        </button>
        {issuesOpen && (<>
        <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid="issue-form">
          <Input value={issue.text} onChange={(e) => setIssue({ ...issue, text: e.target.value })} placeholder="Describe the issue…" className="h-9" data-testid="blocker-text-input" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={issue.owner_id} onValueChange={(v) => setIssue({ ...issue, owner_id: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="issue-owner-select"><SelectValue placeholder={phase.owner_name || "Owner"} /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" className="h-8 text-xs" value={issue.due_date} onChange={(e) => setIssue({ ...issue, due_date: e.target.value })} data-testid="issue-due-input" />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={issue.blocking} onCheckedChange={(c) => setIssue({ ...issue, blocking: !!c })} data-testid="blocker-critical-checkbox" />Blocking — prevents phase completion</label>
            <Button size="sm" variant="outline" onClick={addBlocker} data-testid="blocker-add-btn"><AlertOctagon className="h-4 w-4 mr-1" />Add</Button>
          </div>
        </div>
        <div className="mt-2 space-y-2">
          {phase.blockers.map((b) => {
            const blocking = b.blocking ?? b.critical;
            return (
            <div key={b.blocker_id} className={`rounded-md border p-2.5 text-sm ${b.open ? (blocking ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50") : "border-slate-200 bg-white opacity-60"}`} data-testid={`blocker-row-${b.blocker_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className={b.open ? "text-slate-900" : "line-through text-slate-500"}>{b.text}</div>
                  <div className="text-[11px] text-slate-500">
                    <span className={blocking ? "font-semibold text-red-700" : ""}>{blocking ? "Blocking" : "Non-blocking"}</span>
                    {b.owner_name && <> · {b.owner_name}</>}{b.due_date && <> · due <span className="font-mono">{b.due_date}</span></>}
                    {" · "}{b.open ? "Open" : `Resolved ${fmtDate(b.resolved_at)}`}
                  </div>
                </div>
                {b.open && <Button size="sm" variant="ghost" className="h-7" onClick={() => resolve(b)} title="Mark resolved" data-testid={`blocker-resolve-${b.blocker_id}`}><Check className="h-4 w-4" /></Button>}
              </div>
            </div>
          );})}
        </div>
        </>)}
        {phase.history?.length > 0 && (
          <div className="mt-5">
            <p className="overline">Phase history</p>
            <ul className="mt-2 space-y-1 text-[11px] text-slate-500">
              {[...phase.history].reverse().map((h, i) => (
                <li key={i}><span className="font-mono">{fmtDate(h.at)}</span> · {h.by}: {h.from.replace(/_/g, " ")} → <strong>{h.to.replace(/_/g, " ")}</strong>{h.reason ? ` (${h.reason})` : ""}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};
